import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiX, FiMinus } from 'react-icons/fi';
import ChatMessage from './ChatMessage';
import JuliaActivity from './JuliaActivity';
import ChatInput from './ChatInput';
import { chatApi } from '../../api/chat';
import { ChatMessage as ChatMessageType } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { CHARACTERS } from '../../assets/characters';

interface ChatWindowProps {
  onClose: () => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [minimized, setMinimized] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSend = async (content: string) => {
    const userMessage: ChatMessageType = {
      id: Date.now().toString(),
      conversation_id: conversationId || '',
      role: 'user',
      content,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsTyping(true);

    try {
      const response = await chatApi.sendMessage(content, conversationId);
      const d = response.data as any;
      setConversationId(String(d.conversation_id || d.conversation?.id || ''));
      setMessages((prev) => [...prev, d.message]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          conversation_id: conversationId || '',
          role: 'assistant',
          content: 'Désolé, une erreur est survenue. Veuillez réessayer.',
          created_at: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const userName = user ? `${user.first_name} ${user.last_name}` : 'Vous';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      style={{
        position: 'fixed',
        bottom: 100,
        right: 24,
        width: 380,
        height: minimized ? 'auto' : 520,
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 1001,
        border: '1px solid var(--color-border-light)',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1rem 1.25rem',
          background: 'var(--color-primary)',
          color: '#fff',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <img
            src={CHARACTERS.julia}
            alt="Julia"
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              objectFit: 'contain',
              objectPosition: 'center bottom',
              background: 'rgba(255,255,255,0.2)',
              flexShrink: 0,
            }}
          />
          <div>
            <p style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Julia IA</p>
            <p style={{ fontSize: 'var(--font-size-xs)', opacity: 0.8 }}>Assistante RH intelligente</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <button
            onClick={() => setMinimized(!minimized)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 'var(--radius-sm)',
              color: '#fff',
              opacity: 0.8,
              transition: 'opacity var(--transition-fast)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.8'; }}
          >
            <FiMinus size={16} />
          </button>
          <button
            onClick={onClose}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 'var(--radius-sm)',
              color: '#fff',
              opacity: 0.8,
              transition: 'opacity var(--transition-fast)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.8'; }}
          >
            <FiX size={16} />
          </button>
        </div>
      </div>

      {/* Messages */}
      {!minimized && (
        <>
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '0.5rem 0',
            }}
          >
            {messages.length === 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  padding: '2rem',
                  textAlign: 'center',
                }}
              >
                <img
                  src={CHARACTERS.julia}
                  alt="Julia"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    objectFit: 'contain',
                    objectPosition: 'center bottom',
                    background: 'var(--color-bg)',
                    marginBottom: '1rem',
                    display: 'block',
                  }}
                />
                <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Bonjour ! Je suis Julia.</p>
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                  Votre assistante RH et juridique. Posez-moi vos questions sur le droit du travail,
                  la gestion RH ou vos obligations.
                </p>
              </div>
            )}
            {messages.map((msg) => (
              <ChatMessage
                key={msg.id}
                role={msg.role}
                content={msg.content}
                userName={userName}
                metadata={msg.metadata}
              />
            ))}
            {isTyping && <JuliaActivity active />}
            <div ref={messagesEndRef} />
          </div>
          <ChatInput onSend={handleSend} disabled={isTyping} />
        </>
      )}
    </motion.div>
  );
};

export default ChatWindow;
